import { create } from 'zustand';

/**
 * Active in-app drag sources, SHARED across every FileTree mounted in the
 * sidebar. Each tree subscribes to Tauri's onDropFiles bridge independently
 * and hit-tests against its own DOM; when the user drags a row from tree A
 * onto a row in tree B, B needs to read A's sources to actually run the
 * paste. A local-per-tree ref couldn't satisfy that, hence the shared store.
 *
 * Writes:
 *   - `setSources(paths)` on the source row's `dragstart`.
 *   - `clearSources()` from the bridge's `onDrop` / `onLeave` after the
 *     drop runner (or a no-op leave) has consumed the value.
 *
 * Reads:
 *   - The owning tree's row render (so the source row dims via
 *     `data-drag-source`).
 *   - Every tree's `applyDragHover` / drop runner (so cross-root drops
 *     can resolve `sources` from anywhere).
 */
export interface FsDragStore {
  /** Absolute source paths for the in-flight drag (empty when no drag). */
  sources: readonly string[];
  /** Replace the source list. Synchronous; readable on the next event tick. */
  setSources: (paths: readonly string[]) => void;
  /** Empty the source list. Idempotent. */
  clearSources: () => void;
}

export const useFsDragStore = create<FsDragStore>((set) => ({
  sources: [],
  setSources: (paths) => set({ sources: [...paths] }),
  clearSources: () => set((s) => (s.sources.length === 0 ? s : { sources: [] })),
}));
